// Genesis Appliance Repair · Blog post content
// Each post ships both English and Spanish copy, plus a Markdown body.
// Linked at /blog/:slug.

export const POSTS = [
  {
    slug: "fridge-runs-warmer-in-kona",
    minutesRead: 6,
    publishedAt: "2026-02-12T08:00:00-10:00",
    dateModified: "2026-02-12T08:00:00-10:00",
    image:
      "https://images.unsplash.com/photo-1571175443880-49e1d25b2bc5?auto=format&fit=crop&w=1600&q=85",
    tag: { en: "Refrigerators", es: "Refrigeradores" },
    date: { en: "Feb 12, 2026", es: "12 Feb, 2026" },
    title: {
      en: "Why your fridge runs warmer in a Kona kitchen (and the simple fix)",
      es: "Por qué su refrigerador funciona más caliente en una cocina de Kona (y la solución simple)",
    },
    excerpt: {
      en: "Tropical climates and high humidity are a quiet threat to refrigeration. Here's how we re-calibrate compressors for Hawaii homes.",
      es: "El clima tropical y la humedad alta son una amenaza silenciosa para la refrigeración. Así recalibramos compresores para hogares de Hawái.",
    },
    seo: {
      en: {
        title: "Refrigerator Running Warm in Kona? Genesis Repair Guide",
        description:
          "Big Island homeowners: here's why your fridge fights its compressor in Kona's humidity, and the Genesis technician fix that restores cool, quiet cycles.",
      },
      es: {
        title: "¿Refrigerador caliente en Kona? Guía Genesis",
        description:
          "Propietarios de la Isla Grande: por qué su refrigerador lucha contra el compresor con la humedad de Kona, y la solución de los técnicos Genesis.",
      },
    },
    body: {
      en: `## The hidden cost of Kona's humidity

A refrigerator is, at its core, a humidity-management machine. Its compressor moves heat out of the box and into your kitchen. In a temperate mainland climate, the gradient between your kitchen air and your fridge interior is gentle. In a Kona kitchen, with 70 to 85 percent relative humidity for much of the year, that gradient is steep, and the compressor pays the price.

We see three quiet patterns on the Big Island:

1. **Compressors that never rest.** Run cycles that should last seven to nine minutes stretch to twelve or fifteen.
2. **Condenser coils caked in salt and dust.** Coastal air carries fine particulate that bonds to oily coil surfaces.
3. **Door seals that lose their grip earlier.** Heat and moisture age the gasket faster than published lifespans suggest.

## How we re-calibrate for Hawaii

When a Genesis technician services your fridge, we treat it as an island-specific machine, not a factory unit. Our process:

- **Coil clean and inspection.** We pull the unit, vacuum the coils, and check the condenser fan motor for the slow degradation that high humidity accelerates.
- **Gasket evaluation.** A dollar bill test on every door panel. If it slides out without resistance, the gasket is no longer earning its keep.
- **Thermistor calibration.** We verify temperatures at three points in the cabinet and on the evaporator. Drift of even two degrees changes your compressor's duty cycle dramatically.
- **Defrost cycle confirmation.** Tropical climates push more moisture into the freezer. We confirm your defrost heater and timer are running on schedule.

## What you can do today

Two habits that quietly extend your fridge's life on the Big Island:

- Pull the unit away from the wall once a quarter and vacuum the back coils.
- Keep the temperature dial one notch warmer than you think you need. Most modern fridges over-cool, and the compressor will thank you.

If your fridge is running warm, humming louder than usual, or sweating around the door, that's the time to book. The earlier we calibrate, the longer your compressor lasts.`,
      es: `## El costo oculto de la humedad de Kona

Un refrigerador es, en esencia, una máquina de gestión de humedad. Su compresor extrae calor de la caja y lo lleva a su cocina. En un clima continental templado, el gradiente entre el aire de su cocina y el interior del refrigerador es suave. En una cocina de Kona, con humedad relativa del 70 al 85 por ciento gran parte del año, ese gradiente es pronunciado y el compresor paga el precio.

Vemos tres patrones silenciosos en la Isla Grande:

1. **Compresores que nunca descansan.** Ciclos que deberían durar de siete a nueve minutos se extienden a doce o quince.
2. **Bobinas del condensador cubiertas de sal y polvo.** El aire costero lleva partículas finas que se adhieren a las superficies aceitosas.
3. **Sellos de puerta que pierden agarre antes.** El calor y la humedad envejecen el empaque más rápido de lo que sugieren las especificaciones.

## Cómo recalibramos para Hawái

Cuando un técnico de Genesis revisa su refrigerador, lo tratamos como una máquina específica de la isla, no como una unidad de fábrica. Nuestro proceso:

- **Limpieza e inspección de bobinas.** Movemos la unidad, aspiramos las bobinas y revisamos el motor del ventilador del condensador.
- **Evaluación del empaque.** Una prueba con billete en cada panel de la puerta. Si se desliza sin resistencia, el empaque ya no cumple su función.
- **Calibración del termistor.** Verificamos temperaturas en tres puntos del gabinete y del evaporador. Una desviación de dos grados cambia drásticamente el ciclo de trabajo del compresor.
- **Confirmación del ciclo de descongelado.** Los climas tropicales empujan más humedad al congelador. Confirmamos que el calentador y el temporizador funcionan a tiempo.

## Lo que usted puede hacer hoy

Dos hábitos que extienden la vida útil de su refrigerador en la Isla Grande:

- Separe la unidad de la pared una vez por trimestre y aspire las bobinas traseras.
- Mantenga el termostato un escalón más cálido de lo que cree necesitar. La mayoría de los refrigeradores modernos enfrían en exceso, y el compresor lo agradecerá.

Si su refrigerador está caliente, zumbando más fuerte de lo habitual o transpirando en la puerta, ese es el momento de reservar. Cuanto antes calibramos, más durará su compresor.`,
    },
  },
  {
    slug: "5-washer-sounds-you-should-never-ignore",
    minutesRead: 5,
    publishedAt: "2026-01-30T08:00:00-10:00",
    dateModified: "2026-01-30T08:00:00-10:00",
    image:
      "https://images.unsplash.com/photo-1626806787461-102c1bfaaea1?auto=format&fit=crop&w=1600&q=85",
    tag: { en: "Washers", es: "Lavadoras" },
    date: { en: "Jan 30, 2026", es: "30 Ene, 2026" },
    title: {
      en: "5 sounds your washer makes that you should never ignore",
      es: "5 sonidos de su lavadora que nunca debe ignorar",
    },
    excerpt: {
      en: "From the gentle thunk to the metallic shriek, here's what each noise means and when it's time to book a technician.",
      es: "Desde el golpe suave hasta el chillido metálico, qué significa cada ruido y cuándo es hora de llamar a un técnico.",
    },
    seo: {
      en: {
        title: "5 Washer Sounds You Should Never Ignore | Genesis Hawaii",
        description:
          "Banging, grinding, shrieking, humming, clicking. Our Big Island technicians decode the five washer sounds that signal real trouble, before they get expensive.",
      },
      es: {
        title: "5 Sonidos de Lavadora que No Debe Ignorar | Genesis Hawái",
        description:
          "Golpes, rechinidos, chillidos, zumbidos y clics. Nuestros técnicos de la Isla Grande explican los cinco sonidos de lavadora que anuncian problemas reales.",
      },
    },
    body: {
      en: `Most washer problems announce themselves through sound long before a flooded laundry room does. Here are the five sounds we hear most often on the Big Island, and what each one usually means.

## 1. A heavy thunk at the start of the spin

That dull thump is almost always an unbalanced load. Once in a while, it's a sign that one of the four suspension rods has lost tension. If the noise persists with a half-empty drum, book a technician.

## 2. Grinding or grating during the wash cycle

This is the sound of metal contacting something it shouldn't. The two most common causes are a worn drive belt and a failing tub bearing. Tub bearings rarely fail quickly, so catching this sound early saves you a much larger repair later.

## 3. A high-pitched metallic shriek during spin

Almost always a bearing problem, sometimes a worn shock absorber. Continuing to use a washer in this state can warp the drum, which turns a routine repair into a full replacement.

## 4. A continuous low humming with no water filling

The pump motor is trying to run but the impeller is stuck or the inlet valve has failed. Most of the time this is a 30-minute service call. Left alone, it cooks the motor windings.

## 5. Sharp clicking during the rinse cycle

That's the door lock solenoid or a relay on the control board. Modern washers will refuse to start the next cycle until this is fixed. Clicking that happens repeatedly with the door already locked usually points to the control board.

## When to call us

If any of these sounds has been with you for more than a week, book a visit. Washers are forgiving until they aren't, and bearing or drum damage compounds fast on a high-cycle Big Island household.`,
      es: `La mayoría de los problemas de lavadora se anuncian con sonido mucho antes de que aparezca una inundación. Estos son los cinco sonidos que más escuchamos en la Isla Grande y qué significa cada uno.

## 1. Un golpe sordo al inicio del centrifugado

Ese golpe apagado casi siempre es una carga desbalanceada. De vez en cuando, indica que una de las cuatro barras de suspensión perdió tensión. Si el ruido persiste con el tambor medio vacío, llame a un técnico.

## 2. Rechinido o roce durante el ciclo de lavado

Es el sonido de metal contra algo que no debería tocar. Las dos causas más comunes son una banda de transmisión gastada y un rodamiento de tambor fallando. Detectarlo temprano evita una reparación mayor más adelante.

## 3. Un chillido metálico agudo durante el centrifugado

Casi siempre un problema de rodamiento, a veces un amortiguador gastado. Continuar usando la lavadora en este estado puede deformar el tambor y convertir una reparación de rutina en un reemplazo completo.

## 4. Un zumbido bajo y continuo sin que entre agua

El motor de la bomba intenta funcionar pero el impulsor está atascado o la válvula de entrada falló. La mayoría de las veces es una visita de 30 minutos. Si se ignora, quema los devanados del motor.

## 5. Clic fuerte durante el ciclo de enjuague

Es el solenoide del seguro de la puerta o un relé en la tarjeta de control. Las lavadoras modernas se niegan a iniciar el siguiente ciclo hasta que se repare. Clics repetidos con la puerta ya cerrada suelen apuntar a la tarjeta.

## Cuándo llamarnos

Si alguno de estos sonidos lo ha acompañado por más de una semana, reserve una visita. Las lavadoras perdonan hasta que ya no, y el daño al rodamiento o al tambor se acumula rápido en un hogar de la Isla Grande.`,
    },
  },
  {
    slug: "saltwater-air-and-the-dryer-vent-rule",
    minutesRead: 4,
    publishedAt: "2026-01-18T08:00:00-10:00",
    dateModified: "2026-01-18T08:00:00-10:00",
    image:
      "https://images.unsplash.com/photo-1610557892470-55d9e80c0bce?auto=format&fit=crop&w=1600&q=85",
    tag: { en: "Dryers", es: "Secadoras" },
    date: { en: "Jan 18, 2026", es: "18 Ene, 2026" },
    title: {
      en: "Saltwater air, lint, and the dryer vent rule for Waimea",
      es: "Aire salino, pelusa y la regla del ducto de secadora en Waimea",
    },
    excerpt: {
      en: "Coastal living means more frequent vent maintenance. Here's the schedule we recommend, and the safety reason behind it.",
      es: "Vivir en la costa significa mantenimiento más frecuente de la ventilación. Aquí está el calendario que recomendamos.",
    },
    seo: {
      en: {
        title: "Dryer Vent Maintenance for Coastal Hawaii Homes | Genesis",
        description:
          "Saltwater air accelerates lint buildup in Big Island dryers. Here's the maintenance schedule our technicians recommend for Waimea, Kona, and coastal homes.",
      },
      es: {
        title: "Mantenimiento del Ducto de Secadora en la Costa de Hawái | Genesis",
        description:
          "El aire salino acelera la acumulación de pelusa en las secadoras de la Isla Grande. Este es el calendario que recomendamos para Waimea, Kona y zonas costeras.",
      },
    },
    body: {
      en: `Coastal Hawaii is a beautiful place to live and a tough place to run a dryer. Salt-bearing air corrodes vent ducts faster than mainland averages, while frequent rain and humidity force your dryer to work longer on every load. The result is more lint, more friction, and more heat.

## Why coastal homes need a different schedule

The U.S. Fire Administration recommends an annual dryer vent inspection for the average household. On the Big Island, we recommend twice a year for inland homes and quarterly for properties within a mile of the coast. The reason is simple: every load throws lint into a duct that is also collecting micro-corrosion. Once that builds up, the dryer's safety thermostat starts cycling, drying takes 50 percent longer, and the risk of a vent fire climbs.

## What we check on a vent service

- The full duct run from the dryer to the exterior cap.
- The exterior cap, where birds and insects build nests during the dry season.
- The lint trap housing, including the deep portion most homeowners can't reach.
- The high-limit thermostat, which often shows wear from running close to its limit.

## Two habits that keep you safe

1. Clean the lint screen before every load. Yes, every load.
2. Run the dryer with the laundry room door open during long cycles. It improves airflow and helps the unit reach its target temperature faster.

If your dryer is suddenly taking two cycles to dry what one used to handle, call us. That symptom is the leading indicator of a vent that needs service.`,
      es: `La costa de Hawái es un lugar hermoso para vivir y duro para una secadora. El aire con sal corroe los ductos más rápido que el promedio continental, mientras la lluvia frecuente y la humedad obligan a la secadora a trabajar más en cada carga. El resultado es más pelusa, más fricción y más calor.

## Por qué los hogares costeros necesitan un calendario diferente

La Administración de Bomberos de EE. UU. recomienda inspección anual del ducto de secadora para el hogar promedio. En la Isla Grande, recomendamos dos veces al año para hogares interiores y trimestralmente para propiedades a menos de una milla de la costa. La razón es simple: cada carga lanza pelusa a un ducto que también acumula microcorrosión. Una vez que se acumula, el termostato de seguridad de la secadora comienza a ciclar, el secado tarda 50 por ciento más y el riesgo de incendio aumenta.

## Lo que revisamos en un servicio de ventilación

- El recorrido completo del ducto desde la secadora hasta la salida exterior.
- La salida exterior, donde aves e insectos construyen nidos durante la estación seca.
- La carcasa del filtro de pelusa, incluyendo la parte profunda que la mayoría no alcanza.
- El termostato de límite alto, que suele mostrar desgaste por funcionar cerca de su tope.

## Dos hábitos que lo mantienen seguro

1. Limpie el filtro de pelusa antes de cada carga. Sí, cada carga.
2. Use la secadora con la puerta de la lavandería abierta durante ciclos largos. Mejora el flujo de aire.

Si su secadora ahora tarda dos ciclos para secar lo que antes lograba en uno, llámenos. Ese síntoma es el indicador principal de un ducto que necesita servicio.`,
    },
  },
  {
    slug: "dishwasher-film-on-glassware",
    minutesRead: 4,
    image:
      "https://images.unsplash.com/photo-1581622558663-b2e33377dfb2?auto=format&fit=crop&w=1600&q=85",
    tag: { en: "Dishwashers", es: "Lavavajillas" },
    date: { en: "Jan 4, 2026", es: "4 Ene, 2026" },
    title: {
      en: "Why your dishwasher leaves a film on glassware (it's not the soap)",
      es: "Por qué su lavavajillas deja una película en la cristalería (no es el jabón)",
    },
    excerpt: {
      en: "Big Island water chemistry plays a role. We walk through the three repairs that solve 90% of the cases we see.",
      es: "La química del agua de la Isla Grande influye. Repasamos las tres reparaciones que resuelven el 90% de los casos.",
    },
    seo: {
      en: {
        title: "Dishwasher Leaves Film on Glasses? 3 Fixes | Genesis Hawaii",
        description:
          "Cloudy glassware on the Big Island is rarely a detergent problem. Our technicians walk through the three dishwasher repairs that solve 90% of cases.",
      },
      es: {
        title: "¿Lavavajillas Deja Película en los Vasos? 3 Soluciones | Genesis",
        description:
          "La cristalería opaca en la Isla Grande rara vez es problema del detergente. Nuestros técnicos explican las tres reparaciones que resuelven el 90% de los casos.",
      },
    },
    body: {
      en: `Cloudy glassware is almost never the detergent's fault. Nine times out of ten, when we're called for a film complaint, the underlying issue is one of three repairs.

## 1. A water heater set too low

Your dishwasher needs water at roughly 120 to 125 degrees Fahrenheit at the spray arms to dissolve detergent properly. Many Big Island water heaters are set to 110 to save energy. The dishwasher then runs colder, leaving minerals on glass. We can verify your inlet temperature in five minutes.

## 2. A failing heating element

Built-in dishwashers heat water during the cycle. When the heating element starts to fail, it usually does so quietly, and the only symptom you notice is film. We test resistance at the element and confirm the high-limit thermostat is in spec.

## 3. A spray arm that is no longer spraying evenly

Mineral deposits clog the spray arm jets. We pull the arms, inspect the rotation bearings, and clear the jets. On about a third of service calls we also replace the lower arm because the bearing has worn enough to slow its rotation.

## What about rinse aid?

Rinse aid helps, but it's a finishing touch, not a fix. If you've added rinse aid and you're still seeing film, the problem is mechanical. Book a service visit and we'll diagnose it on the spot.`,
      es: `La cristalería opaca casi nunca es culpa del detergente. Nueve de cada diez veces, cuando nos llaman por una película blanca, el problema real es una de estas tres reparaciones.

## 1. Un calentador de agua ajustado demasiado bajo

Su lavavajillas necesita agua a aproximadamente 120 a 125 grados Fahrenheit en los brazos aspersores para disolver el detergente. Muchos calentadores de la Isla Grande están en 110 para ahorrar energía. El lavavajillas funciona más frío y deja minerales en el vidrio. Podemos verificar la temperatura de entrada en cinco minutos.

## 2. Un elemento calentador fallando

Los lavavajillas empotrados calientan el agua durante el ciclo. Cuando el elemento empieza a fallar, lo hace silenciosamente, y el único síntoma es la película. Probamos la resistencia y confirmamos el termostato de límite alto.

## 3. Un brazo aspersor que ya no rocía uniformemente

Los depósitos minerales obstruyen los chorros del brazo. Sacamos los brazos, revisamos los rodamientos de rotación y limpiamos los chorros. En aproximadamente un tercio de los casos reemplazamos el brazo inferior porque el rodamiento se ha desgastado.

## ¿Y el abrillantador?

El abrillantador ayuda, pero es un toque final, no una solución. Si ya lo está usando y aún ve la película, el problema es mecánico. Reserve una visita y lo diagnosticamos en el momento.`,
    },
  },
  {
    slug: "test-if-your-oven-temperature-is-accurate",
    minutesRead: 3,
    publishedAt: "2025-12-22T08:00:00-10:00",
    dateModified: "2025-12-22T08:00:00-10:00",
    image:
      "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?auto=format&fit=crop&w=1600&q=85",
    tag: { en: "Range & Oven", es: "Estufas y Hornos" },
    date: { en: "Dec 22, 2025", es: "22 Dic, 2025" },
    title: {
      en: "How to safely test if your oven temperature is accurate",
      es: "Cómo probar de forma segura si la temperatura de su horno es precisa",
    },
    excerpt: {
      en: "A simple at-home test that tells you whether your oven needs a recalibration or a full service visit.",
      es: "Una prueba sencilla en casa que indica si su horno necesita recalibración o una visita de servicio.",
    },
    seo: {
      en: {
        title: "Test Your Oven Temperature Accuracy at Home | Genesis Hawaii",
        description:
          "A safe 20-minute test that tells you whether your oven simply needs recalibration or a full Big Island service visit. Step-by-step from Genesis technicians.",
      },
      es: {
        title: "Prueba Casera de Temperatura de Horno | Genesis Hawái",
        description:
          "Una prueba segura de 20 minutos que indica si su horno necesita recalibración o una visita de servicio completa. Paso a paso de los técnicos Genesis.",
      },
    },
    body: {
      en: `If your cookies brown on one side or your roast takes longer than the recipe promises, your oven temperature has likely drifted. Here is the safe test we recommend before booking a service visit.

## What you'll need

- An inexpensive standalone oven thermometer (not the built-in display).
- A heat-safe surface to place the thermometer on the middle rack.

## The test

1. Place the thermometer on the middle rack, centered front to back.
2. Set the oven to 350 F and let it preheat for at least 15 minutes after the indicator says ready. Most ovens reach the set point well before they actually stabilize.
3. Read the thermometer without opening the oven door more than a crack.
4. Repeat the reading at 25 and 35 minutes.

## How to interpret the result

- **Within 10 F of 350**: your oven is fine, recipes may simply need recipe-specific adjustment.
- **15 to 25 F off**: a recalibration through the control panel usually solves it. We can walk you through it remotely if you'd like.
- **More than 25 F off, or swinging in either direction**: book a service visit. The most common culprits are a failing temperature sensor, a worn bake or broil element, or a control board with drifting reference voltage.

If you smell gas, see flame from a burner that should be off, or hear unfamiliar clicking, stop the test and book a visit immediately. Calibration is a small repair. Gas safety is not.`,
      es: `Si sus galletas se doran de un lado o el asado tarda más de lo que promete la receta, la temperatura de su horno probablemente se desvió. Esta es la prueba segura que recomendamos antes de reservar.

## Lo que necesita

- Un termómetro de horno independiente económico (no el display integrado).
- Una superficie resistente al calor para colocarlo en la rejilla del medio.

## La prueba

1. Coloque el termómetro en la rejilla del medio, centrado.
2. Ajuste el horno a 350 F y déjelo precalentar al menos 15 minutos después de que indique listo. La mayoría llega al punto antes de estabilizarse.
3. Lea el termómetro sin abrir la puerta más que una rendija.
4. Repita la lectura a los 25 y 35 minutos.

## Cómo interpretar el resultado

- **Dentro de 10 F de 350**: su horno está bien, las recetas pueden necesitar ajuste específico.
- **15 a 25 F de diferencia**: una recalibración por el panel de control suele resolverlo. Podemos guiarle remotamente.
- **Más de 25 F, o variando en ambas direcciones**: reserve una visita. Las causas más comunes son un sensor de temperatura fallando, un elemento de horneado o asado gastado, o una tarjeta de control con voltaje de referencia desviado.

Si huele gas, ve llama en un quemador que debería estar apagado, o escucha clics inusuales, detenga la prueba y reserve de inmediato. La calibración es una reparación menor. La seguridad del gas, no.`,
    },
  },
  {
    slug: "repair-vs-replace-big-island-rule-of-thumb",
    minutesRead: 5,
    publishedAt: "2025-12-10T08:00:00-10:00",
    dateModified: "2025-12-10T08:00:00-10:00",
    image:
      "https://images.unsplash.com/photo-1556909190-eccf4a8bf97a?auto=format&fit=crop&w=1600&q=85",
    tag: { en: "Buying Guide", es: "Guía de compra" },
    date: { en: "Dec 10, 2025", es: "10 Dic, 2025" },
    title: {
      en: "When repair beats replacement: a Big Island rule of thumb",
      es: "Cuándo reparar gana a reemplazar: una regla para la Isla Grande",
    },
    excerpt: {
      en: "Most appliances aren't worth replacing. Our technicians share the heuristics they use on every service call.",
      es: "La mayoría de los electrodomésticos no valen la pena reemplazar. Nuestros técnicos comparten las heurísticas que usan.",
    },
    seo: {
      en: {
        title: "Repair vs Replace: Big Island Appliance Rule of Thumb | Genesis",
        description:
          "Should you repair or replace? Our Big Island technicians share the simple heuristics they use on every appliance call so you spend wisely.",
      },
      es: {
        title: "¿Reparar o Reemplazar? Regla de la Isla Grande | Genesis",
        description:
          "¿Reparar o reemplazar? Nuestros técnicos de la Isla Grande comparten las heurísticas simples que usan en cada visita para que gaste con criterio.",
      },
    },
    body: {
      en: `The honest answer is: most of the time, repair wins. New appliances are expensive, shipping to the Big Island is expensive, and replacement adds 90 percent of a working machine to a landfill. Here is the framework we use on every call.

## The 50 percent rule

If the repair costs more than 50 percent of the price of a comparable new unit, and the appliance is more than 8 years old, replacement is usually the right call. Otherwise, repair almost always pencils out.

## The three quiet exceptions

1. **Refrigerators older than 12 years** with a failed sealed system (compressor, condenser, evaporator). These repairs are expensive and the rest of the unit is usually close to retirement too.
2. **Top-load washers** with a failed tub bearing on a 10-plus year old unit. The labor is significant.
3. **Wall ovens** with a failed control board on a unit older than 10 years. Replacement parts are often back-ordered and the cabinet cutout may not match modern dimensions.

## What you don't pay enough attention to

- **Reliability of the brand model**, not just the brand. Two units from the same maker can have very different repair histories.
- **Energy consumption.** A fridge from 2014 already costs less to run than one from 2008. If your unit is from before 2010, modern energy savings can offset part of a replacement.
- **Big Island delivery and install timelines.** A new unit can take three to six weeks to land. A repair, in our hands, takes days.

Call us before you call the showroom. We'll quote the repair, give you an honest opinion on whether it's worth it, and never recommend replacement to pad a bill.`,
      es: `La respuesta honesta es: la mayoría de las veces, reparar gana. Los electrodomésticos nuevos son costosos, el envío a la Isla Grande es costoso y el reemplazo manda al vertedero el 90 por ciento de una máquina que funciona. Este es el marco que usamos en cada visita.

## La regla del 50 por ciento

Si la reparación cuesta más del 50 por ciento del precio de una unidad nueva comparable, y el electrodoméstico tiene más de 8 años, el reemplazo suele ser la mejor opción. De lo contrario, reparar casi siempre conviene.

## Las tres excepciones silenciosas

1. **Refrigeradores de más de 12 años** con sistema sellado fallado (compresor, condensador, evaporador). Esas reparaciones son caras y el resto de la unidad suele estar cerca del retiro.
2. **Lavadoras de carga superior** con rodamiento de tambor fallado en una unidad de 10 años o más. La mano de obra es significativa.
3. **Hornos de pared** con tarjeta de control fallada en una unidad de más de 10 años. Las piezas de repuesto suelen estar agotadas y el hueco del gabinete puede no coincidir con dimensiones modernas.

## A lo que no presta suficiente atención

- **Confiabilidad del modelo específico**, no solo la marca. Dos unidades del mismo fabricante pueden tener historiales muy diferentes.
- **Consumo de energía.** Un refrigerador de 2014 ya cuesta menos operar que uno de 2008. Si el suyo es anterior a 2010, los ahorros modernos compensan parte del reemplazo.
- **Tiempos de entrega e instalación en la Isla Grande.** Una unidad nueva puede tardar de tres a seis semanas. Una reparación, en nuestras manos, toma días.

Llámenos antes de ir a la tienda. Cotizamos la reparación, le damos una opinión honesta y nunca recomendamos reemplazo para inflar una cuenta.`,
    },
  },
];

export const getPostBySlug = (slug) => POSTS.find((p) => p.slug === slug);

export const getPostIndex = (slug) => POSTS.findIndex((p) => p.slug === slug);
